import { JournodexGeographyModel } from '../../../Common/Journodex/models/journodex-geography.model';
import { AddressApiModel, PrivateDataApiModel } from '../../../api/targeting/model/private-data-api.model';
import { HandleModel } from './handle.model';
import { UserNoteModel } from '../../../Common/Misc/models/user-note.model';
import { PersonalizationValue } from '../../../api/distributions/model/distribution-personalization.api.model';
import { PersonalizationTagEnum } from '../../../Discover/Targeting/enums/personalization-tag.enum';

export class PrivateDataModel {
    handles: HandleModel[];
    notes: UserNoteModel[];
    addresses: AddressModel[];

    // personalization fields
    namePrefix: string = '';
    firstName: string = '';
    lastName: string = '';
    outletName: string = '';


    private arrayCompareFields = [
        'handles',
        'addresses',
        'notes',
    ];

    constructor(apiModel?: PrivateDataApiModel) {
        if (apiModel) {
            this.handles = apiModel.handles.map(handle => new HandleModel(handle, true));
            this.notes = apiModel.notes.map(note => new UserNoteModel(note));
            this.addresses = apiModel.addresses.map(address => new AddressModel(address));

            this.namePrefix = apiModel.personalizations.find(x => x.tagType === PersonalizationTagEnum.PREFIX)?.value || '';
            this.firstName = apiModel.personalizations.find(x => x.tagType === PersonalizationTagEnum.FNAME)?.value || '';
            this.lastName = apiModel.personalizations.find(x => x.tagType === PersonalizationTagEnum.LNAME)?.value || '';
            this.outletName = apiModel.personalizations.find(x => x.tagType === PersonalizationTagEnum.OUTLET_NAME)?.value || '';
        }
    }

    toApiModel(forClone: boolean = false) {
        const personalizations: PersonalizationValue[] = [];

        if (this.namePrefix.length > 0) {
            personalizations.push({
                tagType: PersonalizationTagEnum.PREFIX,
                value: this.namePrefix
            });
        }

        if (this.firstName.length > 0) {
            personalizations.push({
                tagType: PersonalizationTagEnum.FNAME,
                value: this.firstName
            });
        }

        if (this.lastName.length > 0) {
            personalizations.push({
                tagType: PersonalizationTagEnum.LNAME,
                value: this.lastName
            });
        }

        if (this.outletName.length > 0) {
            personalizations.push({
                tagType: PersonalizationTagEnum.OUTLET_NAME,
                value: this.outletName
            });
        }

        return {
            handles: this.handles.map(handle => handle.toApiModel()),
            notes: this.notes.map(note => note.toApiModel()),
            addresses: forClone ? this.addresses.map(address => address.toApiModel())
                : this.addresses.map(address => address.toApiModelForPrivateData()),
            personalizations: personalizations
        };
    }

    clone() {
        const clone = new PrivateDataModel(this.toApiModel(true));
        clone.handles.forEach(handle => {
            handle.isPrivateData = true;
        });

        return clone;
    }

    equals(other: PrivateDataModel): boolean {
        // Order invariant equality
        for (const field of this.arrayCompareFields) {
            const left: any[] = this[field] ? this[field] : [];
            const right: any[] = other[field] ? other[field] : [];
            if (left.length === right.length) {
                for (const element of left) {
                    if (!right.find(x => x.equals(element))) {
                        return false;
                    }
                }
            } else {
                return false;
            }
        }

        if (this.namePrefix !== other.namePrefix) {
            return false;
        }

        if (this.firstName !== other.firstName) {
            return false;
        }

        if (this.lastName !== other.lastName) {
            return false;
        }

        if (this.outletName !== other.outletName) {
            return false;
        }

        return true;
    }
}

export class AddressModel {
    addressLine1?: string;
    addressLine2?: string;
    postalCode?: string;
    geography?: JournodexGeographyModel;

    constructor(apiModel: AddressApiModel) {
        if (apiModel) {
            this.addressLine1 = apiModel.addressLine1;
            this.addressLine2 = apiModel.addressLine2;
            this.postalCode = apiModel.postalCode;
            this.geography = new JournodexGeographyModel(apiModel.geography);
        }
    }

    toApiModel() {
        return {
            addressLine1: this.addressLine1 || null,
            addressLine2: this.addressLine2 || null,
            postalCode: this.postalCode || null,
            geography: this.geography.toApiEntity() || null
        };
    }

    toApiModelForPrivateData() {
        return {
            addressLine1: this.addressLine1 || null,
            addressLine2: this.addressLine2 || null,
            postalCode: this.postalCode || null,
            geography: this.geography?.collectionId ? { id: this.geography.collectionId } : null
        };
    }

    equals(other): boolean {
        if (!other) {
            return false;
        }

        return (
            this.addressLine1 === other.addressLine1
            && this.addressLine2 === other.addressLine2
            && this.postalCode === other.postalCode
            && this.geography?.collectionId === other.geography?.collectionId
        );
    }
}
